import { Component, Input, OnChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  OwnershipChangeTypeToOwnershipMilestoneIds,
  OwnershipChangeTypeToStageToMilestoneIds,
  OwnershipMilestoneId,
  OwnershipMilestoneIdToStage,
  OwnershipMilestoneIdToTitle,
} from '../../model/divestment/OwnershipMilestoneId';
import { TimelineStage, timelineStageToLabel } from '../../model/divestment/TimelineStage';
import { TimelineFormCtrlSuffix } from './DivestmentTabData';
import { OwnershipShareEntry } from '../../../ag-grid-support/ownership-share/ownership-share-tracking-table/OwnershipShareTrackingTableConfig';
import { TimelineApproach } from '../../model/divestment/TimelineApproach';
import { TimelineStatus } from '../../model/divestment/TimelineStatus';
import { OwnershipMilestoneStatus } from '../../model/divestment/OwnershipMilestone';
import {
  OwnershipChangeType,
  ownershipChangeTypeToLabel,
} from '../../model/ownership-share-tracking/OwnershipChangeType';
import { ValidatorUtil } from '../../ValidatorUtil';

@Component({
  selector: 'digires-divestment-timeline',
  templateUrl: './divestment-timeline.component.html',
  styleUrls: ['./divestment-timeline.component.scss'],
})
export class DivestmentTimelineComponent implements OnChanges {
  readonly allStages = Object.values(TimelineStage);
  readonly lastTimelineStage = TimelineStage.COMPLETE;
  readonly stageToLabel = timelineStageToLabel;
  readonly milestoneTitle = OwnershipMilestoneIdToTitle;

  @Input() entry: OwnershipShareEntry;
  @Input() readonly: boolean;

  formGroup: FormGroup;
  dateFormCtrls: FormControl[] = [];
  activeStage: TimelineStage = null;
  activeMilestoneId: OwnershipMilestoneId;
  stageToMilestoneIds: Record<TimelineStage, OwnershipMilestoneId[]>;

  ngOnChanges() {
    this.formGroup = new FormGroup({
      approachType: new FormControl(this.entry.timelineApproach === TimelineApproach.FLEXIBLE),
      nboApproval: new FormControl(this.entry.timelineNboApproval),
      boApproval: new FormControl(this.entry.timelineBoApproval),
      comment: new FormControl(this.entry.timelineComment),
      timelineEndComment: new FormControl(this.entry.timelineEndComment),
      rejected: new FormControl(this.entry.timelineStatus === TimelineStatus.REJECTED),
      acquired: new FormControl(
        this.entry.timelineStatus === TimelineStatus.ACQUIRED || this.entry.timelineStatus === TimelineStatus.DIVESTED,
      ),
    });

    this.stageToMilestoneIds = OwnershipChangeTypeToStageToMilestoneIds[this.entry.type];
    const milestoneIds = OwnershipChangeTypeToOwnershipMilestoneIds[this.entry.type];
    for (const id of milestoneIds) {
      const ms = this.entry.timelineMilestones[id] || { id: id, date: null, status: OwnershipMilestoneStatus.FORECAST };
      const idDate = id + TimelineFormCtrlSuffix.date;
      const idNa = id + TimelineFormCtrlSuffix.na;
      const idComplete = id + TimelineFormCtrlSuffix.complete;
      this.formGroup.addControl(idDate, new FormControl(ms.date, [ValidatorUtil.validateDate]));
      this.formGroup.addControl(idNa, new FormControl(ms.status === OwnershipMilestoneStatus.NOT_APPLICABLE));
      this.formGroup.addControl(idComplete, new FormControl(ms.status === OwnershipMilestoneStatus.COMPLETE));
    }
    this.dateFormCtrls = milestoneIds.map((symbolicName) => {
      const formCtrlName = symbolicName + TimelineFormCtrlSuffix.date;
      return this.formGroup.get(formCtrlName) as FormControl;
    });
    this.dateFormCtrls.push(this.formGroup.get('nboApproval') as FormControl);
    this.dateFormCtrls.push(this.formGroup.get('boApproval') as FormControl);
    this.calculateCurrentStageAndMs();
  }

  onMilestoneChange(): void {
    this.calculateCurrentStageAndMs();
    for (const ctrl of this.dateFormCtrls) {
      ctrl.updateValueAndValidity();
    }
  }

  public calculateCurrentStageAndMs(): void {
    if (!this.formGroup) {
      return;
    }
    const fgValue = this.formGroup.value;
    const milestoneIds = OwnershipChangeTypeToOwnershipMilestoneIds[this.entry.type];
    const lastMilestoneId = milestoneIds[milestoneIds.length - 1];

    let currentId = milestoneIds[0];
    // fast-path: if End-Of-Process is completed then we are done.
    if (fgValue.rejected || fgValue.acquired) {
      currentId = lastMilestoneId;
    } else {
      for (const msSymName of milestoneIds) {
        currentId = msSymName;
        if (!fgValue[msSymName + TimelineFormCtrlSuffix.na] && !fgValue[msSymName + TimelineFormCtrlSuffix.complete]) {
          break;
        }
      }
    }
    this.activeMilestoneId = currentId;
    this.activeStage = OwnershipMilestoneIdToStage[currentId];
  }

  isApproachTypeDirty(): boolean {
    return this.formGroup.get('approachType').dirty;
  }

  isCommentDirty(): boolean {
    return this.formGroup.get('comment').dirty;
  }

  isEndOfProcessCommentDirty(): boolean {
    return this.formGroup.get('timelineEndComment').dirty;
  }

  isCompleted(): boolean {
    const milestoneIds = OwnershipChangeTypeToOwnershipMilestoneIds[this.entry.type];
    const lastMilestoneId = milestoneIds[milestoneIds.length - 1];
    const fgValue = this.formGroup.value;
    return fgValue[lastMilestoneId + TimelineFormCtrlSuffix.complete];
  }

  isReadOnly(propName: string): boolean {
    return false;
  }

  getReadOnlyReason(propName: string): string {
    if (!this.isReadOnly(propName)) {
      return undefined;
    }
    if (propName === 'timelineEndComment') {
      return 'This field can only be filled in after the End of Process milestone is complete.';
    }
    return null;
  }

  getTypeLabel(type: OwnershipChangeType): string {
    return ownershipChangeTypeToLabel[type];
  }
}
